import { noop, v2_ProductDetail } from '@crocodille/common';
import { useTranslation } from 'react-i18next';
import Modal from '../../elements/modal/modal';
import { numberFormatter } from '@utils/format';
import { Maybe, isSome } from '@crocodille/common';
import { NutritionalValues } from '@crocodille/api/lib/model/product';

import styles from './product-nutritional-values.module.scss';

interface NutritionalValuesProps {
  readonly product: v2_ProductDetail;
  onClose?(): void;
  readonly open?: boolean;
  readonly configurationValue?: Maybe<string>;
  readonly optionsNutritionValues?: (NutritionalValues | null)[];
}

const calculateTotalNutritionalValues = (
  base: Maybe<NutritionalValues>,
  additions: (NutritionalValues | null)[],
) => {
  if (!base) {
    return {
      perPortion: {},
      per100g: {},
    };
  }

  const initializeValues = (source: NutritionalValues) =>
    Object.entries(source ?? {}).reduce<{
      [key: string]: number | null;
    }>((acc, [key, value]) => {
      acc[key] = typeof value === 'number' ? value : null;
      return acc;
    }, {});

  const resultPerPortion = initializeValues(base);
  const resultPer100g = initializeValues(base);

  const validAdditions = additions.filter(
    (item) =>
      item && item.portion && Object.values(item).some((val) => val !== null),
  );

  const isValueValid = (key: keyof NutritionalValues): boolean => {
    return (
      base?.[key] !== null || validAdditions.some((a) => a?.[key] !== null)
    );
  };

  Object.keys(resultPerPortion).forEach((key) => {
    const typedKey = key as keyof NutritionalValues;
    const baseValue = base?.[typedKey];
    if (typeof baseValue === 'number' && typeof base.portion === 'number') {
      resultPerPortion[typedKey] = (baseValue * base.portion) / 100;
    } else {
      resultPerPortion[typedKey] = null;
    }
  });

  validAdditions.forEach((item) => {
    Object.keys(resultPerPortion).forEach((key) => {
      const typedKey = key as keyof NutritionalValues;
      const addValue = item?.[typedKey];
      const portion = item?.portion;

      if (
        typeof addValue === 'number' &&
        typeof portion === 'number' &&
        isValueValid(typedKey)
      ) {
        if (typeof resultPerPortion[typedKey] === 'number') {
          resultPerPortion[typedKey]! += (addValue * portion) / 100;
        } else {
          resultPerPortion[typedKey] = (addValue * portion) / 100;
        }

        if (typeof resultPer100g[typedKey] === 'number') {
          resultPer100g[typedKey]! += addValue;
        } else {
          resultPer100g[typedKey] = addValue;
        }
      }
    });
  });

  Object.keys(resultPer100g).forEach((key) => {
    const typedKey = key as keyof NutritionalValues;
    if (
      typeof resultPer100g[typedKey] === 'number' &&
      validAdditions.length > 0
    ) {
      resultPer100g[typedKey]! /= validAdditions.length;
    } else {
      const baseValue = base?.[typedKey];
      resultPer100g[typedKey] =
        typeof baseValue === 'number' ? baseValue : null;
    }
  });

  return {
    perPortion: resultPerPortion,
    per100g: resultPer100g,
  };
};

const NutritionalValuesTable: React.FC<
  React.PropsWithChildren<NutritionalValuesProps>
> = ({
  product,
  onClose = noop,
  open = false,
  configurationValue,
  optionsNutritionValues = [],
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { perPortion, per100g } = calculateTotalNutritionalValues(
    product.nutritionalValues,
    optionsNutritionValues ?? [],
  );

  const energyConversionFactors: { [key: string]: number } = {
    energy: 8400,
    energyKcal: 2008,
    fat: 70,
    saturatedFattyAcid: 20,
    carbohydrate: 270,
    sugar: 90,
    fiber: 25,
    protein: 50,
    salt: 6,
  };

  const calculateNutrientPercentRI = (
    key: string,
    value: number,
  ): number | null => {
    const factor = energyConversionFactors[key];
    return factor ? Math.round((value / factor) * 100) : null;
  };

  const getSign = (key: string) => {
    switch (key) {
      case 'energy':
        return 'kJ';
      case 'energyKcal':
        return 'kcal';
      default:
        return 'g';
    }
  };

  const excludedKeys = ['id', 'portion', 'imageUrl'];

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeOnOutsideClick={true}
      showCloseButton
      height="fill"
      width="auto"
      transparent
      title={''}
      hideTitle
    >
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.contentInner}>
            <div className={styles.titleContainer}>
              <h2 className={styles.title}>{product.title}</h2>
              {isSome(configurationValue) ? (
                <div>{configurationValue}</div>
              ) : null}
            </div>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th></th>
                  <th>{t('product.nutritional_values.portion')}</th>
                  <th>{t('product.nutritional_values.grams')}</th>
                  <th>% RI *</th>
                </tr>
              </thead>
              <tbody>
                {perPortion &&
                  Object.entries(perPortion)
                    .filter(([key]) => !excludedKeys.includes(key))
                    .map(([key, value]) => {
                      const numericValue = Number(value);
                      const percentRI = calculateNutrientPercentRI(
                        key,
                        numericValue,
                      );

                      return (
                        <tr key={key}>
                          <td>
                            {t(`product.nutritional_values.${key}`) || key}
                          </td>

                          <td>
                            {isSome(value) ? (
                              <span>
                                {numberFormatter(language, numericValue)}{' '}
                                {getSign(key)}
                              </span>
                            ) : (
                              <span className={styles.textCenter}>/</span>
                            )}
                          </td>

                          <td>
                            {isSome(per100g[key]) ? (
                              <span>
                                {numberFormatter(language, per100g[key] ?? 0)}{' '}
                                {getSign(key)}
                              </span>
                            ) : (
                              <span className={styles.textCenter}>/</span>
                            )}
                          </td>

                          <td>
                            {percentRI !== null && isSome(value)
                              ? `${percentRI}`
                              : '/'}
                          </td>
                        </tr>
                      );
                    })}
              </tbody>
            </table>
            <div className={styles.note}>
              {t('product.nutritional_values.note')}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default NutritionalValuesTable;
